import { createQueryKeys } from "@lukemorales/query-key-factory"

import axios from "@/api/axios"
import { APIFn, PaginatedAPIFn } from "@/api/utils"
import { MasterclassAttendance } from "@/features/teachers/types"

import {
  AssessmentListResponse,
  AssessmentParticipantsType,
  Content,
  CPATOrganisationLevelReport,
  ItemType,
  OrganizationItemDetailsType,
  OrganizationItemPlannerType,
  OrganizationItemProgressAnalytics,
  OrganizationItemTopPerformers,
  OrganizationItemUserAnalytics,
  OrganizationUserMasterclassResponse,
  ReminderData,
  RetrieveMasterclassData,
  UserAssessmentReportType,
  UserCourseAnalyticsList,
  UserCourseAnalyticsOverviewType,
  UserMasterclassAnalytics,
} from "./types"

export const ContentQueryKey = createQueryKeys("content", {
  list: ({ orgId }: { orgId: string }) => ({
    queryKey: ["content-list", orgId],
  }),

  retrieveMasterclass: ({ slug }: { slug: string }) => ({
    queryKey: ["retrieve-masterclass", slug],
  }),

  assessmentList: ({ id, orgId }: { id?: string; orgId: string }) => ({
    queryKey: ["assessment-list", id, orgId],
  }),

  sendReminder: ({
    itemId,
    itemType,
    orgId,
  }: {
    itemId?: string
    itemType: ItemType
    orgId: string
  }) => ({ queryKey: ["send-reminder", itemId, itemType, orgId] }),

  retrieveReminder: ({
    itemId,
    itemType,
    orgId,
  }: {
    itemId?: string
    itemType: ItemType
    orgId: string
  }) => ({ queryKey: ["retrieve-reminder", itemId, itemType, orgId] }),

  retrieveOrganizationAssessment: ({
    assessmentKey,
    orgId,
  }: {
    assessmentKey?: number | string
    orgId: string
  }) => ({
    queryKey: ["retrieve-organization-assessment", assessmentKey, orgId],
  }),

  retrieveUserAssessmentReport: ({
    userId,
    orgId,
  }: {
    userId?: number | string
    orgId: string
  }) => ({
    queryKey: ["retrieve-user-assessment-report", userId, orgId],
  }),

  retrieveAssessmentParticipants: ({
    params,
    orgId,
  }: {
    params: Record<string, any>
    orgId: string
  }) => ({
    queryKey: ["retrieve-assessment-participants", params, orgId],
  }),

  listOrganizationUserMasterclasses: ({
    userId,
    orgId,
  }: {
    userId: string
    orgId: string
  }) => ({
    queryKey: ["list-organization-user-masterclasses", userId, orgId],
  }),

  retrieveUserMasterclassAnalytics: ({
    userId,
    orgId,
  }: {
    userId: string
    orgId: string
  }) => ({
    queryKey: ["retrieve-user-masterclass-analytics", userId, orgId],
  }),

  masterclassAttendeesList: ({
    masterclassId,
    params,
    orgId,
  }: {
    masterclassId: string
    params?: Record<string, any>
    orgId: string
  }) => ({
    queryKey: ["masterclass-attendees-list", masterclassId, params, orgId],
  }),

  retrieveOrganizationItemDetails: ({
    itemType,
    itemId,
    orgId,
  }: {
    itemType: string
    itemId: string
    orgId: string
  }) => ({
    queryKey: ["retrieve-organization-item-details", itemType, itemId, orgId],
  }),

  retrieveOrganizationItemPlannerDetails: ({
    itemType,
    itemId,
    orgId,
  }: {
    itemType: string
    itemId: string
    orgId: string
  }) => ({
    queryKey: [
      "retrieve-organization-item-planner-details",
      itemType,
      itemId,
      orgId,
    ],
  }),

  retrieveOrganizationItemProgressAnalytics: (props, orgId) => ({
    queryKey: ["retrieve-organization-item-progress-analytics", props, orgId],
  }),

  retrieveOrganizationItemUserAnalytics: ({
    itemType,
    itemId,
    orgId,
  }: {
    itemType: string
    itemId: string
    orgId: string
  }) => ({
    queryKey: [
      "retrieve-organization-item-user-analytics",
      itemType,
      itemId,
      orgId,
    ],
  }),

  retrieveOrganizationItemTopPerformers: (props, orgId) => ({
    queryKey: ["retrieve-organization-item-top-performers", props, orgId],
  }),

  retrieveUserCourseAnalyticsOverview: ({
    userId,
    orgId,
  }: {
    userId: string
    orgId: string
  }) => ({
    queryKey: ["retrieve-user-course-analytics-overview", userId, orgId],
  }),

  retrieveUserCourseAnalyticsList: ({
    userId,
    current,
    orgId,
  }: {
    userId: string
    current?: boolean
    orgId: string
  }) => ({
    queryKey: ["retrieve-user-course-analytics-list", userId, current, orgId],
  }),
})

type Resource = {
  list: APIFn<Content[], { orgId: string }>

  retrieveMasterclass: APIFn<RetrieveMasterclassData, { slug: string }>

  sendReminder: APIFn<ReminderData, { orgId: string }>

  retrieveReminder: APIFn<
    ReminderData,
    { itemId?: string; itemType: ItemType; orgId: string }
  >

  assessmentList: APIFn<AssessmentListResponse[], { orgId: string }>

  retrieveCPATOrganisationReport: APIFn<
    CPATOrganisationLevelReport,
    { orgId: string }
  >

  retrieveUserAssessmentReport: APIFn<
    UserAssessmentReportType[],
    { userId: string; orgId: string }
  >

  retrieveAssessmentParticipants: PaginatedAPIFn<
    AssessmentParticipantsType[],
    { assessmentId: string; orgId: string }
  >

  listOrganizationUserMasterclasses: APIFn<
    OrganizationUserMasterclassResponse[],
    { userId: string; orgId: string }
  >

  retrieveUserMasterclassAnalytics: APIFn<
    UserMasterclassAnalytics,
    { userId: string; orgId: string }
  >

  masterclassAttendeesList: PaginatedAPIFn<
    MasterclassAttendance[],
    { masterclassId: string; orgId: string }
  >

  retrieveOrganizationItemDetails: APIFn<
    OrganizationItemDetailsType,
    {
      itemType: "course"
      itemId: string
      orgId: string
    }
  >

  retrieveOrganizationItemPlannerDetails: APIFn<
    OrganizationItemPlannerType[],
    {
      itemType: "course"
      itemId: string
      orgId: string
    }
  >

  retrieveOrganizationItemProgressAnalytics: APIFn<
    OrganizationItemProgressAnalytics,
    {
      itemType: "course"
      itemId: string
      orgId: string
    }
  >

  retrieveOrganizationItemUserAnalytics: APIFn<
    OrganizationItemUserAnalytics[],
    {
      itemType: "course"
      itemId: string
      orgId: string
    }
  >

  retrieveOrganizationItemTopPerformers: APIFn<
    OrganizationItemTopPerformers,
    {
      itemType: "course"
      itemId: string
      orgId: string
    }
  >

  retrieveUserCourseAnalyticsOverview: APIFn<
    UserCourseAnalyticsOverviewType,
    {
      userId: string
      orgId: string
    }
  >

  retrieveUserCourseAnalyticsList: APIFn<
    UserCourseAnalyticsList[],
    {
      userId: string
      orgId: string
    }
  >
}

export const ContentResource: Resource = {
  list: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/learning-contents/`,
      config,
    ),

  retrieveMasterclass: ({ urlParams, ...config }) =>
    axios.get(`/cygnus/v1/masterclasses/${urlParams.slug}/`, config),

  assessmentList: ({ urlParams, ...config }) =>
    axios.get(
      `/perseus/v1/assessments/organizations/${urlParams.orgId}`,
      config,
    ),

  sendReminder: ({ urlParams, data, ...config }) =>
    axios.post(`/eris/v1/organizations/${urlParams.orgId}/logs/`, data, config),

  retrieveReminder: ({ urlParams, ...config }) =>
    axios.get(
      `/eris/v1/organizations/${urlParams.orgId}/items/${urlParams.itemType}/${urlParams.itemId}/logs/latest/`,
      config,
    ),

  retrieveCPATOrganisationReport: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/assessments/reports/`,
      config,
    ),

  retrieveUserAssessmentReport: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/users/${urlParams.userId}/assessments/reports/`,
      config,
    ),

  retrieveAssessmentParticipants: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/assessments/${urlParams.assessmentId}/users/`,
      config,
    ),

  listOrganizationUserMasterclasses: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/users/${urlParams.userId}/masterclasses/`,
      config,
    ),

  retrieveUserMasterclassAnalytics: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/users/${urlParams.userId}/masterclasses/analytics/`,
      config,
    ),

  masterclassAttendeesList: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/masterclasses/${urlParams.masterclassId}/users/`,
      config,
    ),

  retrieveOrganizationItemDetails: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/${urlParams.itemType}/${urlParams.itemId}/details/`,
      config,
    ),

  retrieveOrganizationItemPlannerDetails: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/${urlParams.itemType}/${urlParams.itemId}/planner-details/`,
      config,
    ),

  retrieveOrganizationItemProgressAnalytics: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/${urlParams.itemType}/${urlParams.itemId}/progress-analytics/`,
      config,
    ),

  retrieveOrganizationItemUserAnalytics: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/${urlParams.itemType}/${urlParams.itemId}/user-analytics/`,
      config,
    ),

  retrieveOrganizationItemTopPerformers: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/${urlParams.itemType}/${urlParams.itemId}/score-analytics/`,
      config,
    ),

  retrieveUserCourseAnalyticsOverview: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/users/${urlParams.userId}/courses/overview/`,
      config,
    ),

  retrieveUserCourseAnalyticsList: ({ urlParams, ...config }) =>
    axios.get(
      `/v1/organizations/${urlParams.orgId}/users/${urlParams.userId}/courses/analytics/`,
      config,
    ),
}
